import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../hooks/use-stores';

import {
    SpaceBetweenDiv,
    FlexDiv,
    Heading1,
    TextLead,
    TextDefault,
    ThemedButton,
} from '@awarego-ui/components';
import styled from 'styled-components';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    TextField,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ThemedSpinner from '../components/themed/ThemedSpinner';

const ImageAsLink = styled.img`
    cursor: pointer;
`;

const useStyles = (modalWidth) => makeStyles(() => ({
    paper: {
        minWidth: modalWidth || 672,
        maxWidth: modalWidth ? modalWidth + 100 : undefined,
        padding: 24,
    },
}))();

function ConfirmationModal() {
    const { commonStore } = useStores();
    
    const [passcodeValue, setPasscodeValue] = useState('');
    const [additionalCheckboxChecked, setAdditionalCheckboxChecked] =
        useState(false);

    const {
        confirmationRequired,
        confirmationDescription,
        confirmationAction,
        options,
        confirmationTitle,
        confirmationShowCancel,
        additionalCheckbox,
        isConfirmationDelete,
        confirmationPasscode,
    } = commonStore;

    const { confirmationCancelAction = 'Cancel', modalWidth } = options;
    const classes = useStyles(modalWidth);

    const handleClose = () => {
        commonStore.cancelConfirm();
    };

    const onConfirm = (arg) => {
        const value = passcodeValue;
        setPasscodeValue('');
        if (!commonStore.additionalCheckbox || additionalCheckboxChecked)
            commonStore.doConfirm(arg, value);
    };

    const renderAction = (confirmationAction) => {
        if (Array.isArray(confirmationAction)) {
            return confirmationAction.map((action) => (
                <ThemedButton
                    key={action.key}
                    mode={isConfirmationDelete ? 'red' : 'primary'}
                    onClick={() => onConfirm(action.key)}
                    text={action.value}
                />
            ));
        } else if (confirmationAction) {
            return (
                <ThemedButton
                    mode={isConfirmationDelete ? 'red' : 'primary'}
                    onClick={() => onConfirm()}
                    text={confirmationAction}
                />
            );
        }
        return null;
    };

    const toggleAdditionalCheckboxChecked = () => {
        setAdditionalCheckboxChecked(!additionalCheckboxChecked);
    };

    if (!confirmationRequired) return null;

    return (
        <Dialog
            open
            onClose={handleClose}
            classes={{ paper: classes.paper }}
        >
            <DialogTitle>
                <SpaceBetweenDiv>
                    <Heading1>{confirmationTitle}</Heading1>
                    <ImageAsLink
                        src="/static/img/close.svg"
                        alt="Close"
                        onClick={handleClose}
                    />
                </SpaceBetweenDiv>
            </DialogTitle>

            <DialogContent>
                {commonStore.confirmationLoading ? (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '30px 0',
                        }}
                    >
                        <ThemedSpinner />
                        {typeof commonStore.confirmationLoading === 'string' && (
                            <Typography variant="h6" style={{ marginTop: 16 }}>
                                {commonStore.confirmationLoading || 'Loading...'}
                            </Typography>
                        )}
                    </div>
                ) : (
                    <>
                        {options && options.customContent ? (
                            options.customContent
                        ) : (
                            <TextLead>{confirmationDescription}</TextLead>
                        )}

                        {confirmationPasscode && (
                            <div style={{ marginTop: 20 }}>
                                <Typography variant="body1">
                                    Please type <strong>{confirmationPasscode}</strong> to confirm:
                                </Typography>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    value={passcodeValue}
                                    onChange={(e) => setPasscodeValue(e.target.value)}
                                    fullWidth
                                    style={{ marginTop: 8 }}
                                />
                            </div>
                        )}

                        {additionalCheckbox && (
                            <label htmlFor={'additionalCheckbox'}>
                                <input
                                    type="checkbox"
                                    id={'additionalCheckbox'}
                                    checked={additionalCheckboxChecked}
                                    onChange={toggleAdditionalCheckboxChecked}
                                    style={{ marginRight: 8 }}
                                />
                                {additionalCheckbox}
                            </label>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                {confirmationShowCancel && (
                    <FlexDiv pr={40}>
                        <TextDefault
                            uppercase
                            extraBold
                            link
                            onClick={handleClose}
                        >
                            {confirmationCancelAction}
                        </TextDefault>
                    </FlexDiv>
                )}
                {!commonStore.confirmationLoading && renderAction(confirmationAction)}
            </DialogActions>
        </Dialog>
    );
}

export default observer(ConfirmationModal);
